import {
  ARK_API_COMPANY_SEARCH_URL,
  ARK_API_DOCS_COMPANY_SEARCH_URL,
  ARK_API_DOCS_PEOPLE_SEARCH_URL,
  ARK_API_DOCS_URL,
  ARK_API_PEOPLE_SEARCH_URL,
} from '@aibees/elephant/constants/api-panel';
import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { transform as companyRequestTransform } from '@aibees/elephant/feature/search-table-company/hooks/use-browse-company-search';
import { transform as peopleRequestTransform } from '@aibees/elephant/feature/search-table-people/hooks/use-browse-people-search';
import {
  parseReadableJsonString,
  toReadableJsonString,
} from '@aibees/elephant/feature/search-filters/readable-search-json';
import {
  useSearchModeFromRoute,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks/use-obtain-search-type-from-route';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import {
  useSearchParamsStore,
  type SearchTableScope,
} from '@aibees/elephant/store/search-params-store';
import { SearchType, type SearchBrowseParams } from '@aibees/elephant/types';
import { jsonStringify, objSanitizeNilValues } from '@aibees/shared/utils';
import { isEqual, omit } from 'lodash-es';
import { useCallback, useMemo } from 'react';

type ScopeParams = Partial<SearchBrowseParams>;

export type ApplyResult = { ok: true } | { ok: false; errors: string[] };

// First-page envelope — the request the initial search sends. Shown as-is in the
// API Payload tab (live pagination/sort isn't reflected; see doc §3).
const PAGE = 0;
const SIZE = 25;

function isScope(s: string | undefined): s is SearchTableScope {
  return s === 'company' || s === 'people' || s === 'employees';
}

function toArray(value: unknown): string[] | undefined {
  if (Array.isArray(value)) return value as string[];
  return value ? [String(value)] : undefined;
}

/** Snapshot the current scope's params imperatively (no subscription — the
 *  header must not re-render on every keystroke). Mirrors use-share-search-link. */
function readScopeParams(scope: SearchTableScope): ScopeParams {
  const state = (
    useSearchParamsStore as unknown as {
      __store: {
        getState: () => {
          paramsByScope: Record<SearchTableScope, ScopeParams>;
        };
      };
    }
  ).__store.getState();
  return objSanitizeNilValues(state.paramsByScope[scope] ?? {}) as ScopeParams;
}

/**
 * Builds two views of the current search:
 * - `json` — the editable Readable Search JSON (In-App Payload tab).
 * - `apiMethod` / `apiUrl` / `apiBodyJson` — the search as a request to the
 *   public AI Ark API (API Payload tab): the developer-portal endpoint for the
 *   scope, with the body reconstructed via the same `transform()` the in-app
 *   search uses (page/size are the first-page values). `apiDocsUrl` is the
 *   matching API-reference page.
 *
 * `apply` writes an edited/pasted readable payload back via the saved/shared-
 * search path (`replaceParamsForScope` + history reset) so filters/URL/results
 * update. Only the In-App Payload is applyable — the API body can't round-trip.
 */
export function useSearchPayload() {
  const scope = useSearchTableScope();
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();
  const resetHistoryTo = useSearchHistoryStore.useResetTo();

  const { json, currentParams, apiMethod, apiUrl, apiDocsUrl, apiBodyJson } =
    useMemo(() => {
      if (!isScope(scope)) {
        return {
          json: '{}',
          currentParams: {} as ScopeParams,
          apiMethod: 'POST',
          apiUrl: '',
          apiDocsUrl: ARK_API_DOCS_URL,
          apiBodyJson: '{}',
        };
      }

      const params = readScopeParams(scope);

      // company -> /companies ; people & employees -> /people
      const isPeople = type !== SearchType.Company;
      const url = isPeople
        ? ARK_API_PEOPLE_SEARCH_URL
        : ARK_API_COMPANY_SEARCH_URL;
      const docsUrl = isPeople
        ? ARK_API_DOCS_PEOPLE_SEARCH_URL
        : ARK_API_DOCS_COMPANY_SEARCH_URL;

      const reqParams = objSanitizeNilValues(
        omit(params, 'type', 'page', 'size')
      );
      const transformed = (
        isPeople
          ? peopleRequestTransform(reqParams)
          : companyRequestTransform(reqParams, mode)
      ) as Record<string, unknown>;

      const body = isPeople
        ? {
            page: PAGE,
            size: SIZE,
            ...transformed,
            value: toArray(transformed.value),
          }
        : { page: PAGE, size: SIZE, ...transformed };

      return {
        json: toReadableJsonString(scope, params),
        currentParams: params,
        apiMethod: 'POST',
        apiUrl: url,
        apiDocsUrl: docsUrl,
        apiBodyJson: jsonStringify(objSanitizeNilValues(body), 2) ?? '{}',
      };
    }, [scope, type, mode]);

  /** True when the (valid) payload would produce the current active filters — so
   *  Apply is a no-op and should be disabled. Invalid JSON counts as "changed". */
  const matchesCurrent = useCallback(
    (text: string): boolean => {
      if (!isScope(scope)) return false;
      const res = parseReadableJsonString(scope, text);
      return res.ok && isEqual(res.params, currentParams);
    },
    [scope, currentParams]
  );

  const apply = useCallback(
    (text: string): ApplyResult => {
      if (!isScope(scope))
        return { ok: false, errors: ['Unsupported search scope.'] };
      const res = parseReadableJsonString(scope, text);
      if (!res.ok) return { ok: false, errors: res.errors };
      replaceParamsForScope(scope, res.params);
      resetHistoryTo(
        scope,
        objSanitizeNilValues(res.params) as Record<string, string>
      );
      return { ok: true };
    },
    [scope, replaceParamsForScope, resetHistoryTo]
  );

  return {
    json,
    apiMethod,
    apiUrl,
    apiDocsUrl,
    apiBodyJson,
    apply,
    matchesCurrent,
  };
}
